import {useEffect,useState} from 'react';
import {Bell,RefreshCw} from 'lucide-react';
import {supabase} from '../lib/supabase';
import {acknowledgeSyncJobs,syncQueueEnabled,syncQueueOverview,type QueueOverview} from '../lib/syncQueue';

// Mount with key=userId: a late response can never enter another account's UI.
export function CloudSyncNotifications({userId}:{userId:string}){
 const [overview,setOverview]=useState<QueueOverview|null>(null);
 const [saving,setSaving]=useState(false);
 useEffect(()=>{
  const abort=new AbortController();let timer:ReturnType<typeof setTimeout>|undefined,enabled:boolean|undefined,polling=false,hasActive=false,refreshPending=false;
  const poll=async()=>{
   if(polling||abort.signal.aborted)return;polling=true;
   try{
    const session=(await supabase?.auth.getSession())?.data.session;
    if(abort.signal.aborted||session?.user.id!==userId)return;
    const context={userId,token:session.access_token,signal:abort.signal};
    enabled??=await syncQueueEnabled(context);if(!enabled)return;
    const next=await syncQueueOverview(context);hasActive=next.queued+next.running>0;
    if(!abort.signal.aborted)setOverview(next);
   }catch{/* Durable notices remain unread and are fetched on the next visit. */}
   finally{polling=false;if(!abort.signal.aborted&&enabled!==false)timer=setTimeout(poll,refreshPending?0:hasActive&&document.visibilityState==='visible'?15_000:60_000);refreshPending=false;}
  };
  const refresh=()=>{clearTimeout(timer);if(polling)refreshPending=true;else void poll();};
  const onVisible=()=>{if(document.visibilityState==='visible'&&enabled!==false)refresh();};
  const onUpdated=(event:Event)=>{if((event as CustomEvent<string>).detail===userId)refresh();};
  document.addEventListener('visibilitychange',onVisible);
  window.addEventListener('bilaflow:sync-queue-updated',onUpdated);
  void poll();return()=>{abort.abort();clearTimeout(timer);document.removeEventListener('visibilitychange',onVisible);window.removeEventListener('bilaflow:sync-queue-updated',onUpdated);};
 },[userId]);
 if(!overview)return null;
 const success=overview.notifications.filter(j=>j.status==='succeeded'),failed=overview.notifications.filter(j=>j.status!=='succeeded');
 const active=overview.queued+overview.running;
 if(!active&&!overview.review&&!overview.notifications.length)return null;
 const dismiss=async()=>{
  setSaving(true);
  try{
   const session=(await supabase?.auth.getSession())?.data.session;if(session?.user.id!==userId)return;
   const ids=overview.notifications.map(j=>j.id);
   await acknowledgeSyncJobs(ids,{userId,token:session.access_token,signal:AbortSignal.timeout(15_000)});
   setOverview(current=>current?{...current,notifications:current.notifications.filter(j=>!ids.includes(j.id))}:null);
  }catch{/* Keep the notification visible if acknowledgement fails. */}finally{setSaving(false);}
 };
 return <section className={`backup-capacity-warning sync-queue-notice ${failed.length||overview.review?'warning':'healthy'}`} role="status" aria-live="polite" aria-label="แจ้งเตือน Cloud Sync">
  <div className="backup-reminder-icon">{active?<RefreshCw size={22} className="spin"/>:<Bell size={22}/>}</div>
  <div>
   <strong>{failed.length||overview.review?'มีงานซิงค์ที่ต้องตรวจสอบ':active?'กำลังซิงค์เบื้องหลัง':`ซิงค์สำเร็จ ${success.length} รายการ`}</strong>
   {active>0&&<p>รอคิว {overview.queued} · กำลังทำงาน {overview.running} · ใช้งานในเครื่องต่อได้</p>}
   {success.length>0&&active>0&&<p>ซิงค์และตรวจไฟล์สำเร็จแล้ว {success.length} รายการ</p>}
   {(failed.length>0||overview.review>0)&&<p>ยังไม่ยืนยันความสำเร็จ {Math.max(failed.length,overview.review)} รายการ ข้อมูลในเครื่องยังอยู่ กรุณาลองซิงค์ใหม่ หากยังติดให้ติดต่อผู้ดูแล</p>}
   {!active&&!failed.length&&!overview.review&&<p>เอกสารและไฟล์ของรายการเหล่านี้พร้อมบน Cloud แล้ว การแก้ไขใหม่จะซิงค์แยกในรอบถัดไป</p>}
  </div>
  {overview.notifications.length>0&&<button className="btn ghost small" disabled={saving} onClick={()=>void dismiss()}>{saving?'กำลังบันทึก…':'รับทราบ'}</button>}
 </section>;
}
